/**
 * `POST /auth/login` answers **200** with a `status`-discriminated union, so a
 * pending second factor is not an HTTP error — but it carries no tokens
 * either. Left as-is, such a response falls into the mutation's `onSuccess`,
 * sets `accessToken = undefined` (which also clears the user) and silently
 * bounces the visitor back to `/login` with no explanation.
 *
 * `narrowLoginResponse` re-shapes those statuses into the `ApiError` the login
 * form already knows how to render — `LoginPage.describeError` branches on
 * `envelope.requires_2fa` / `requires_2fa_setup`, and that is the only thing
 * that advances the form to its TOTP stage.
 *
 * Lives in its own module (rather than inside `use-auth.ts`) so the admin's
 * node-environment vitest can exercise it without pulling in the React auth
 * provider and its transitive upload/tus imports.
 */

import { ApiError } from '@/lib/api-client';

export interface LoginTokens {
  access_token: string;
  expires_at: string;
  user: unknown;
}

export function narrowLoginResponse<T extends { status?: string }>(raw: T): T {
  if (raw.status === 'requires_2fa') {
    throw new ApiError(401, {
      requires_2fa: true,
      error: { code: 'TWO_FA_REQUIRED', message: 'TOTP code required' },
    });
  }
  // Deprecated since TASK-450 and never emitted by a current API — but an
  // older API image behind a freshly deployed SPA still can. Without this
  // branch the 200 would reach `onSuccess` and LoginPage's deliberately kept
  // fallback would be unreachable.
  if (raw.status === 'requires_2fa_setup') {
    throw new ApiError(401, {
      requires_2fa_setup: true,
      error: { code: 'TWO_FA_SETUP_REQUIRED', message: '2FA setup required' },
    });
  }
  return raw;
}
